import Link from "next/link";
import { Call } from "@/components/icons/Call";
import { Mailbox } from "@/components/icons/Mailbox";
// import { LinkedIn } from "@/components/icons/LinkedIn";
import { Instagram } from "@/components/icons/Instagram";
import { Whatsapp } from "@/components/icons/Whatsapp";
import type { ContactLinkItem, ContactOffice, PolicyContent } from "@/types";

const miscellaneousLinks = [
  { href: "/terms-and-conditions", label: "Terms & Condition" },
  { href: "/privacy-policy", label: "Privacy Policy" },
];

const whatsappSupportLink = "https://wa.link/428o4f";

const navItems = [
  {
    id: 1,
    title: "Own a Device",
    href: "/#device",
  },
  {
    id: 2,
    title: "Our Impact",
    href: "/#impact",
  },
  {
    id: 3,
    title: "FAQs",
    href: "/#faqs",
  },
  {
    id: 4,
    title: "Contact",
    href: "/contact",
  },
];

const navItems2 = [
  {
    id: 2,
    title: "Let's Talk",
    href: whatsappSupportLink,
    icon: Whatsapp,
    target: "_blank" as const,
  },
];

const contactOffices: ContactOffice[] = [
  {
    id: 1,
    name: "Nigeria Office",
    countryEmoji: "🇳🇬",
    addressLines: ["32B Okunola Aina Street,", "Maryland, Lagos."],
    phones: [
      {
        label: "+234-911-388-9461",
        href: "tel:+2349113889461",
      },
      {
        label: "+234-915-842-8464",
        href: "tel:+2349158428464",
      },
      {
        label: "+234-911-388-9467",
        href: "tel:+2349113889467",
      },
    ],
  },
  {
    id: 2,
    name: "Canada Office",
    countryEmoji: "🇨🇦",
    addressLines: ["108 College St, Toronto,", "ON M5G 1L7 Canada"],
    phones: [
      {
        label: "+1 (647) 455 4327",
        href: "tel:+16474554327",
      },
    ],
  },
];

const contactEmailItems: ContactLinkItem[] = [
  {
    label: "hello@usepluck.com",
    href: "mailto:hello@usepluck.com",
  },
];

const contactUsItems = [
  {
    id: 1,
    icon: Call,
    items: contactOffices.flatMap((office) => office.phones),
  },
  {
    id: 2,
    icon: Mailbox,
    items: contactEmailItems,
  },
];

const policyContent: PolicyContent = {
  terms: {
    title: "Terms & Conditions",
    lastUpdatedDate: "Last Updated: 3rd November 2026",
    introText: (
      <>
        <p>
          Welcome to Pluck, a proprietary asset financing platform designed to
          connect Vendors with Customers, optimize transaction processes, and
          enhance the efficiency and quality of vendor–customer interactions
          (referred to as the “Services”).
        </p>
        <p>
          Pluck is a product of Reeddi Platforms Limited (“we”, “us”, “Reeddi”)
          and can be accessed through its website, mobile or other applications
          (“Platform”).
        </p>
        <p>
          We understand that legal documents may be lengthy and boring and often
          you want to skip them, but it is important that you read these terms
          carefully before accessing, downloading, installing, or using the
          Platform. By using this Platform, you (“User”) agree to be legally
          bound by these terms. If you do not agree, you must cease use of the
          Platform immediately.
        </p>
        <p>
          We reserve the right to update, change or replace any part of these
          Terms. It is your responsibility to check this page periodically for
          changes. Your continued use of or access to the Site following the
          posting of any changes constitutes acceptance of these changes.
        </p>
      </>
    ),
    sections: [
      {
        id: "general-terms",
        title: "General Terms",
        content: (
          <div className="policy-clauses">
            <p>
              1.1. By using our Platform in any manner, you confirm that you
              accept these terms of use and that you agree to comply with and be
              bound by them.
            </p>
            <p>
              1.2. You understand that a breach of any of these terms of use may
              result in your rights to use the services on the Platform being
              restricted, suspended, or withdrawn.
            </p>
            <p>
              1.3. By using this Platform, you represent and warrant that you
              are 18 years old and above and are otherwise capable of entering
              into binding contracts including this Terms of Use.
            </p>
            <p>
              1.4. We may amend these terms of use at any time by amending this
              page. Please check this page from time to time to take notice of
              any changes we make as they will be binding on you. If you are an
              existing User, we will duly inform you of any amendments that are
              made, by sending a prompt on the Platform.
            </p>
            <p>1.5. You acknowledge and agree that:</p>
            <ol>
              <li>
                You will not use our services for any illegal or unauthorized
                purpose.
              </li>
              <li>
                You must not transmit any worms or viruses or any code of a
                destructive nature.
              </li>
              <li>
                A breach or violation of any of the terms will result in an
                immediate termination of your account and right to use our
                Services.
              </li>
              <li>
                We have the right, but not the obligation, to take any of the
                following actions in our sole discretion at any time and for any
                reason without giving you any prior notice:
                <ul>
                  <li>
                    Restrict, suspend or terminate your access to all or any
                    part of our services;
                  </li>
                  <li>
                    Change, suspend or discontinue all or any part of our
                    services;
                  </li>
                  <li>
                    Refine, move, or remove any content that is available on all
                    or any part of our services;
                  </li>
                  <li>Deactivate or delete your accounts;</li>
                  <li>
                    Establish general practices and limits concerning use of our
                    services
                  </li>
                </ul>
              </li>
              <li>
                You agree that we will not be liable to you or any third party
                for taking any of these actions.
              </li>
              <li>
                You understand and agree that our services may include
                communications such as service announcements and administrative
                or legal notices from us. Please note that you cannot opt-out of
                receiving these notices.
              </li>
              <li>
                You understand that your content (not including debit card
                information), may be unencrypted and involve (a) transmissions
                over various networks; and (b) changes to conform and adapt to
                technical requirements of connecting networks or devices. Credit
                card information is always encrypted during transfer over
                networks (if applicable).
              </li>
              <li>
                You agree not to reproduce, duplicate, copy, sell, resell or
                exploit any portion of the Platform without express written
                permission by us.
              </li>
              <li>
                You agree not to modify, publish, transmit, reverse engineer,
                participate in the transfer or sale, create derivative works, or
                in any way exploit any of the content, in whole or in part,
                found on the platform. Use of the platform does not entitle
                users to make any unauthorized use of any protected content, and
                in particular, you will not delete or alter any proprietary
                rights or attribution notices in any content. You will use
                protected content solely for your personal use, and will make no
                other use of the content without the express written permission
                of Reeddi and the copyright owner. You agree that you do not
                acquire any ownership rights in any protected content. We do not
                grant you any licenses, express or implied, to the intellectual
                property of Reeddi or our licensors except as expressly
                authorized by these terms.
              </li>
            </ol>
          </div>
        ),
      },
      {
        id: "creating-an-account",
        title: "Creating an Account",
        content: (
          <div className="policy-clauses">
            <p>
              2.1. Once you create an account with us, you are registered on the
              Platform. If you are merely surfing or browsing through the
              Platform and have not yet created an account, your access and use
              of the Platform is still subject to this agreement. If you do not
              agree to this agreement, do not use the Services.
            </p>
            <p>
              2.2. When you register for an account, you will be required to
              provide us with some information about yourself (such as your
              name, date of birth, e-mail address, physical address, phone
              number, debit card information or other personal information).
              Some of this information may be of a confidential nature and may
              include personally identifiable information (“Your Information”).
            </p>
            <p>
              2.3. If you provide Your Information to us then you agree to
              provide true, current, complete and accurate information, and not
              to misrepresent your identity. You also agree to keep Your
              Information current and to update Your Information with us, if it
              changes.
            </p>
            <p>
              2.4. Our collection, use and disclosure of Your Information is
              governed by these Terms & Conditions, our{" "}
              <Link href="/privacy-policy" className="font-medium text-primary">
                Privacy Notice
              </Link>{" "}
              and any applicable law.
            </p>
          </div>
        ),
      },
      {
        id: "account-management",
        title: "Account Management",
        content: (
          <div className="policy-clauses">
            <p>
              3.1. If you have been issued an account by Reeddi in connection
              with your use of the Pluck platform, you are responsible for
              safeguarding your password and any other credentials used to
              access that account, even if you authorize other parties in your
              organization to access your account. You, and not Reeddi, are
              responsible for any activity occurring on your account, including
              but not limited to incidents where your password has been
              compromised. If you become aware of any unauthorized access to
              your account, you should notify Reeddi immediately. Where it is
              determined that such an account has been compromised at your
              instance, you shall be solely responsible for the direct losses
              incurred by Reeddi and others due to any unauthorized use of your
              account.
            </p>
            <p>
              3.2. You acknowledge that Reeddi is not responsible for third
              party access to your account that results from theft or
              misappropriation of your account. Notify us immediately if you
              believe that someone has used your username, email, or password
              without your authorization.
            </p>
            <p>
              3.3. Reeddi may send notices to the email address or text messages
              to your mobile phone registered with your account. You must keep
              your email address, mobile phone number and, where applicable,
              your contact details and payment details associated with your
              account current and accurate.
            </p>
          </div>
        ),
      },
      {
        id: "screening-users",
        title: "Screening Users",
        content: (
          <p>
            Although we retain the right to do so, Reeddi does not screen
            Platform users or verify information communicated through the
            Platform. Reeddi also retains the right to monitor all communication
            and information transmitted using our Platform. Reeddi may, at its
            sole discretion, take steps to verify your identity at any time.
          </p>
        ),
      },
      {
        id: "providing-the-service-to-you",
        title: "Providing the Service to You",
        content: (
          <p>
            You agree and authorize that we may use and disclose information,
            including Personally Identifiable Information, about you for such
            purposes, including making inquiries of third parties concerning
            your identity. You further authorize such third parties to disclose
            to us such information, as we may request for such purposes, and you
            agree to hold them and us harmless from any claim or liability
            arising from the request for or disclosure of such information.
            Unless otherwise agreed under any other agreement signed by the
            parties, you agree that we may terminate your access to the Service
            or Platform at any time, without liability to us, if we are unable
            at any time, to determine or verify your identity. You may contact
            us at{" "}
            <a
              href="mailto:hello@reeddi.com"
              className="font-medium text-primary"
            >
              hello@reeddi.com
            </a>{" "}
            to notify us of inappropriate or illegal conduct or content you
            encounter on the Platform.
          </p>
        ),
      },
      {
        id: "conduct",
        title: "Conduct",
        content: (
          <div className="policy-clauses">
            <p>
              6.1. As a user of our platform, you herein acknowledge, understand
              and agree that all information, text, software, data, photographs,
              messages, tags or any other content, whether it is publicly or
              privately posted and/or transmitted, is the expressed sole
              responsibility of the individual from whom the content originated.
              As such, we do not guarantee the accuracy, integrity or quality of
              such content. It is expressly understood that by use of our
              services, you may be exposed to content including, but not limited
              to, any kind incurred as a result of the use of any content
              provided, emailed, transmitted or otherwise made available by
              Reeddi.
            </p>
            <p>
              6.2. Furthermore, you herein agree not to make use of Pluck for
              the purpose of:
            </p>
            <ol>
              <li>
                uploading, posting, emailing, transmitting, or otherwise making
                available any content that shall be deemed unlawful, harmful,
                threatening, abusing, harassing, tortious, defamatory, vulgar,
                obscene, libelous, or invasive of another’s privacy or which is
                hateful, and/or racially, ethnically or otherwise objectionable;
              </li>
              <li>Causing harm to minors in any manner whatsoever;</li>
              <li>
                Impersonating any individual or entity, or falsely stating or
                otherwise misrepresenting any affiliation with an individual or
                entity;
              </li>
              <li>
                uploading, posting, emailing, transmitting or otherwise offering
                any content that you personally have no right to or that may
                infringe any patent, copyright, trademark, or any other
                proprietary or intellectual rights of any other party;
              </li>
              <li>
                uploading, posting, emailing, transmitting, or otherwise
                offering any unsolicited or unauthorized advertising,
                promotional flyers, junks mail, spam or any other form of
                solicitation;
              </li>
              <li>
                Disrupting the normal flow of communication, or otherwise acting
                in any manner that would negatively affect other users’ ability
                to participate in any real time interactions;
              </li>
              <li>
                Interfering with or disrupting any services on Pluck, servers
                and/or networks that may be connected or related to our website,
                including but not limited to the use of any device software
                and/or routine to bypass the robot exclusion headers;
              </li>
              <li>
                Collecting or storing of any personal data relating to any other
                member or user in connection with the prohibited conduct and/or
                activities which have been set forth in the aforementioned
                paragraphs.
              </li>
            </ol>
            <p>
              6.3. Reeddi hereby reserves the right to access, preserve and/or
              disclose member account information and/or content if it is
              requested to do so by law or in good faith that any such action is
              deemed necessary for:
            </p>
            <ol>
              <li>Compliance with any legal process</li>
              <li>Enforcement of these terms</li>
              <li>
                Responding to any claim that therein contained content is in
                violation of the rights of any third party
              </li>
              <li>
                Protecting the rights, property or the personal safety of Pluck,
                its visitors, users and members including the general public
              </li>
            </ol>
          </div>
        ),
      },
      {
        id: "license-access-and-use",
        title: "Your License, Access and Use of our Platform",
        content: (
          <div className="policy-clauses">
            <p>
              7.1. We own and are the licensor of all rights, titles, and
              interests, in and to the Platform including all rights under
              patents, copyrights, design rights, trade secrets, and all other
              proprietary rights.
            </p>
            <p>
              7.2. Your right to access and use our Platform is personal to you
              and is not transferable by you to any other person or entity. You
              are only entitled to access and use our Platform for lawful
              purposes and pursuant to the terms and conditions of this Terms
              and Conditions and our{" "}
              <Link href="/privacy-policy" className="font-medium text-primary">
                Privacy Notice
              </Link>
              . Any action by you that,
            </p>
            <ol type="i">
              <li>
                violates the terms and conditions of this Terms and/or the
                Privacy Notice;
              </li>
              <li>
                restricts, inhibits or prevents any access, use or enjoyment of
                our Platform; or
              </li>
              <li>
                through the use of our Platform, defames, abuses, harasses,
                offends or threatens others,
              </li>
            </ol>
            <p>
              shall not be permitted, and may result in your loss of the right
              to access and use our Platform.
            </p>
            <p>
              7.3. The rights granted to you in these Terms are subject to
              restrictions. You therefore agree that you shall not and shall not
              encourage or authorize any third party to directly or indirectly:
            </p>
            <ol type="i">
              <li>
                copy, publish, distribute, license, sublicense, sell, resell,
                rent, lease, transfer, assign, host, or otherwise commercially
                exploit the Platform including as a service bureau or
                outsourcing offering or otherwise access or use the Platform
                other than as expressly permitted hereunder;
              </li>
              <li>
                modify, make derivative works of, disassemble, reverse compile
                or reverse engineer any part of the Platform;
              </li>
              <li>
                access the Platform in order to build a similar or competitive
                Platform; and
              </li>
              <li>
                except as expressly stated herein, no part of the Platform may
                be copied, reproduced, distributed, republished, downloaded,
                displayed, posted or transmitted in any form or by any means.
              </li>
            </ol>
            <p>
              Any future release, update, or other addition to functionality of
              the Platform shall be subject to these Terms.
            </p>
            <p>
              7.4. Furthermore, you agree that you will not use any robot,
              spider, scraper, deep link or other similar automated data
              gathering or extraction tools, program, algorithm or methodology
              to access, acquire, copy or monitor our Platform or any portion of
              our Platform or for any other purpose, without our prior written
              permission. Additionally, you agree that you will not:
            </p>
            <ol type="i">
              <li>
                copy, reproduce, modify, create derivative works from,
                distribute or publicly display any content (except for your
                personal information) from our Platform without our prior
                written permission and the appropriate third party, as
                applicable;
              </li>
              <li>
                interfere or attempt to interfere with the proper working of our
                Platform or any activities conducted on our Platform;
              </li>
              <li>
                bypass any robot exclusion headers or other measures we may use
                to prevent or restrict access to our Platform, or
              </li>
              <li>
                interfere or disrupt the Service or servers or networks
                connected to the Platform, including by transmitting any worms,
                viruses, spyware, malware or any other code of a destructive or
                disruptive nature.
              </li>
            </ol>
          </div>
        ),
      },
      {
        id: "unacceptable-use",
        title: "Unacceptable Use of our Platform",
        content: (
          <div className="policy-clauses">
            <p>
              8.1. You shall not and shall not permit any person using your
              device or account to:
            </p>
            <ol type="i">
              <li>
                use this Platform in a way that causes or could cause damage or
                restrict the availability or accessibility of the Platform;
              </li>
              <li>
                extract, mine, scrap proprietary and personal data from this
                Platform without our express and written permission;
              </li>
              <li>
                store, copy, transmit, publish or distribute any computer virus,
                spyware, Trojan horse, worm, keystroke logger or other malicious
                software via this Platform;
              </li>
              <li>
                conduct any fraudulent or illegal activities via the Platform.
              </li>
            </ol>
            <p>
              8.2. You understand and agree that if any fraudulent activity is
              associated with the operation of your account, we shall have the
              right to apply restrictions to your account and report it to the
              appropriate law enforcement agencies.
            </p>
          </div>
        ),
      },
      {
        id: "user-content-rights",
        title: "User Content Rights and Related Responsibilities",
        content: (
          <div className="policy-clauses">
            <p>
              9.1. “User Content”, means, without limitation, any messages,
              texts, digital files, images, photos, personal profile, artwork,
              videos, audio, comments, feedback, suggestions, and documents, or
              any other content you upload, transmit or otherwise make available
              to Reeddi and its users via Pluck. We may, in our sole discretion,
              permit you from time to time, to submit, upload, publish or
              otherwise make available to us, through the Platform any User
              Content. You represent and warrant that you own or otherwise
              control the rights to your User Content and that every part
              thereof, is an original work by you, or you have obtained all
              rights, licenses, consents and permissions necessary to use those
              parts at all times. You further agree to indemnify Reeddi and its
              affiliates for all claims arising from or in connection with any
              claims to any rights in your User Content or any damages arising
              from your User Content.
            </p>
            <p>
              9.2. By submitting User Content on or through the Platform, you
              grant Reeddi a perpetual, irrevocable, transferable, assignable,
              worldwide, non-exclusive, royalty-free license (with the right to
              sublicense through multiple tiers) to access, use, re-use,
              reproduce, transmit, print, publish, display, exhibit, distribute,
              redistribute, copy, host, store, cache, archive, index,
              categorize, comment on, broadcast, stream, download, edit, alter,
              modify, adapt, translate, create derivative works based upon and
              publicly perform such User Content without attribution, and the
              requirement of any permission from or payment to you, or any other
              person or entity, in any manner including, without limitation, for
              commercial, publicity, trade, marketing, promotional, or
              advertising purposes, and in any media now known or hereafter
              devised, under applicable laws.
            </p>
            <p>
              9.3. For us to provide the Service to you, we require that you
              grant us certain rights concerning User Content, including the
              ability to manipulate, process, store and copy User Content to
              provide our Services. Your acceptance of this ‘Terms and
              Conditions’ permits us to do so and grants us any such rights
              necessary to provide the Service to you.
            </p>
            <p>
              9.4. Reeddi expressly disclaims any liability for the loss or
              damage to any User Content or any losses or damages you incur as a
              result of the loss or damage of any User Content. It is your
              responsibility to back up any User Content to prevent its loss.
            </p>
            <p>
              9.5. You are solely responsible for your User Content, including,
              without limitation, comments and feedback.
            </p>
            <p>
              9.6. Reeddi may block, remove or return any User Content at any
              time for any reason whatsoever, or no reason at all. We are not
              responsible for the authenticity, accuracy, completeness,
              appropriateness, or legality of User Content.
            </p>
            <p>9.7. User warrants and agrees not to:</p>
            <ol type="i">
              <li>
                publish falsehoods or misrepresentations that could cause
                injury, loss, or damage to Reeddi or any third party;
              </li>
              <li>
                submit material that is unlawful, obscene, lewd, defamatory,
                libelous, threatening, pornographic, harassing, hateful,
                racially or ethnically offensive, violent, or encourages conduct
                that would be considered a criminal offense, give rise to civil
                liability, violate any law, or is otherwise inappropriate or
                objectionable;
              </li>
              <li>post advertisements or solicitations of business;</li>
              <li>impersonate another person; or</li>
              <li>
                submit copyrighted material, protected by trade secret or
                otherwise subject to third party intellectual property or
                proprietary rights, including privacy and publicity rights
                unless you are the owner of such rights or have permission from
                their rightful owner to post the material and to grant Reeddi
                all of the license rights granted herein.
              </li>
            </ol>
            <p>
              9.8. Notwithstanding the foregoing, Reeddi assumes no
              responsibility for monitoring the Service for inappropriate
              content or modifying or removing such content from the Platform.
            </p>
          </div>
        ),
      },
      {
        id: "interruption-of-service",
        title: "Interruption of Service",
        content: (
          <p>
            Unless otherwise stated in the applicable Service Level Agreement,
            your access and use of our Platform may be interrupted for any of
            several reasons, including, without limitation, the malfunction of
            equipment, periodic updating, maintenance or repair of our Services,
            Software, or other actions that we may elect to take. Reeddi shall
            have no liability for any damage or loss caused as a result of such
            interruptions.
          </p>
        ),
      },
      {
        id: "third-party-links-services-and-content",
        title: "Third Party Links, Services and Content",
        content: (
          <p>
            The Platform may contain features, services and functionalities
            linking you to, or providing you with access to third party services
            and content, websites, directories, servers, networks, systems,
            information, databases, applications, software, programs, courses,
            services, and the Internet as a whole. Because we have no control
            over such sites and resources, we are not responsible for the
            availability of such external sites or resources, and do not endorse
            and are not responsible or liable for any content, advertising,
            courses or other materials on or available from such sites or
            resources. When you visit or use a third party’s website you agree
            to read and consent to the third party’s Terms of Use and Privacy
            Policy and you release us from any liability.
          </p>
        ),
      },
      {
        id: "third-party-integrations",
        title: "Third-Party Integrations, Device/Asset Access",
        content: (
          <div className="policy-clauses">
            <p>
              12.1. You acknowledge and agree that in order to provide
              monitoring, repayment tracking, and device-management services for
              financed devices/assets, we may integrate our Platform with
              third-party service providers through an Application Programming
              Interface (API), including debt-collection and
              device/asset-management providers (“Third-Party Providers”).
            </p>
            <p>
              12.2. Through these integrations, Third-Party Providers may
              receive access to certain device-related information, including
              device/asset identifiers, IMEI numbers, usage status, payment
              status, device location and location history, and other
              information strictly required for repayment monitoring, location
              tracking, recovery, and device/asset-management functions.
              Third-Party Providers may also, where applicable, trigger device
              restrictions or deactivation in accordance with your financing
              agreement.
            </p>
            <p>
              12.3. We shall ensure that any Third-Party Provider accessing such
              data, including device location information, or performing such
              actions is bound by appropriate confidentiality, data-protection,
              and security obligations.
              However, Third-Party Providers operate independently, and their
              collection, use, processing, and control of data or
              device/asset-access functions shall be governed by their own Terms
              of Use and Privacy Policy.
            </p>
            <p>
              12.4. By using the Platform, you expressly consent to the sharing
              of your Information and device/asset information with such
              Third-Party Providers for the purposes described above and
              acknowledge that we are not liable for any actions taken by
              Third-Party Providers within the scope of their authorised
              functionality.
            </p>
          </div>
        ),
      },
      {
        id: "auto-debit-authorisation",
        title: "Auto-Debit Authorisation",
        content: (
          <p>
            By accepting these Terms, you authorise Pluck and its authorised
            payment service providers to automatically debit your account for
            any installment or other amount due under the Asset Loan Agreement.
            You remain responsible for any outstanding amount where an
            auto-debit is unsuccessful.
          </p>
        ),
      },
      {
        id: "limitation-of-liability",
        title: "Limitation of Liability",
        content: (
          <div className="policy-clauses">
            <p>
              14.1. The Pluck Platform is provided on an “as is” and “as
              available” basis. Reeddi does not represent, warrant, or guarantee
              that access to Pluck Platform will be uninterrupted or error free.
              In case of any faults in the software, we will endeavor to correct
              them as soon as possible, but please keep in mind that the
              functioning of the platform may be restricted due to occasional
              technical errors, and we are not able to guarantee that the
              platform will always function.
            </p>
            <p>
              14.2. Reeddi, its representatives, directors and employees are not
              liable for any loss or damage that you may incur because of using
              Pluck, including but not limited to:
            </p>
            <ol type="a">
              <li>any direct or indirect property damage or monetary loss.</li>
              <li>loss of profit.</li>
              <li>
                loss of business, contracts, contacts, goodwill, reputation, and
                any loss that may arise from interruption of the business.
              </li>
              <li>loss or inaccuracy of data; and</li>
              <li>any other type of loss or damage.</li>
            </ol>
            <p>
              14.3. To the maximum extent permitted by law, Reeddi’s total
              aggregate liability arising out of or in connection with these
              Terms or the use of the Platform shall not exceed the total fees
              paid by the User under these Terms during the twelve (12) months
              preceding the event giving rise to the claim.
            </p>
            <p>
              14.4. You agree to fully indemnify and hold Reeddi, its affiliate
              companies, representatives, employees, and directors harmless from
              any claims or losses (including liabilities, damages, costs and
              expenses of any nature) that they suffer as a result of your use
              of Pluck.
            </p>
            <p>
              14.5. Reeddi will immediately terminate your use of Pluck if you
              breach these Terms and Conditions.
            </p>
          </div>
        ),
      },
      {
        id: "intellectual-property-rights",
        title: "Intellectual Property Rights",
        content: (
          <div className="policy-clauses">
            <p>
              15.1. You acknowledge, understand and agree that all of Pluck’s
              trademarks, copyright, trade name, service marks, and other logos
              and any brand features, and/or product and service names are
              trademarks and as such, are and shall remain the property of
              Reeddi. You herein agree not to display and/or use in any manner
              such logo or marks without obtaining Reeddi’s prior written
              consent.
            </p>
            <p>
              15.2. Reeddi will always respect the intellectual property of
              others, and we ask that all of our users do the same. With regards
              to appropriate circumstances and at its sole discretion, Reeddi
              may disable and/or terminate the accounts of any user who violates
              our Terms and/or infringes the rights of others.
            </p>
            <p>
              15.3. If you feel that your work has been duplicated in such a way
              that would constitute copyright infringement, or if you believe
              your intellectual property rights have been otherwise violated,
              you should provide to us the following information:
            </p>
            <ol type="a">
              <li>
                The electronic or physical signature of the individual that is
                authorized on behalf of the owner of the copyright or other
                intellectual property interest;
              </li>
              <li>
                A description of the copyrighted work or other intellectual
                property that you believe has been infringed upon;
              </li>
              <li>
                A description of the location of the site which you allege has
                been infringing upon your work;
              </li>
              <li>
                Your physical address, telephone number and email address;
              </li>
              <li>
                A statement, in which you state that the alleged and disputed
                use of your work is not authorized by the copyright owner, its
                agents or the law; and
              </li>
              <li>
                A statement, made under penalty of perjury, that the
                aforementioned information in your notice is truthful and
                accurate, and that you are the copyright or intellectual
                property owner, representative or agent authorized to act on the
                copyright or intellectual property owner’s behalf.
              </li>
            </ol>
          </div>
        ),
      },
      {
        id: "termination",
        title: "Termination",
        content: (
          <div className="policy-clauses">
            <p>
              16.1. As a user of Pluck, you may cancel or terminate your
              account, associated email address and/or access to our services by
              submitting a cancellation or termination request to{" "}
              <a
                href="mailto:hello@reeddi.com"
                className="font-medium text-primary"
              >
                hello@reeddi.com
              </a>
              .
            </p>
            <p>
              16.2. You agree that Reeddi may, without any prior written notice,
              immediately suspend, terminate, discontinue and/or limit your
              account and access to any of our Services. The cause for such
              termination, discontinuance, suspension and/or limitation of
              access shall include, but is not limited to:
            </p>
            <ol type="a">
              <li>
                any breach or violation of our Terms or any other incorporated
                agreement, regulation and/or guidelines
              </li>
              <li>
                reports from other users as to your objectionable/abusive
                content.
              </li>
              <li>
                by way of requests from law enforcement or any other
                governmental agencies.
              </li>
              <li>
                the discontinuance, alteration, and/or material modification to
                our services, or any part thereof.
              </li>
              <li>unexpected technical or security issues and/or problems.</li>
              <li>any extended periods of inactivity.</li>
              <li>
                any engagement by you in fraudulent or illegal activities,
                and/or.
              </li>
              <li>
                the nonpayment of any associated fees that may be owed by you in
                association with your Pluck account.
              </li>
            </ol>
            <p>
              16.3. You also agree that all termination, suspension,
              discontinuance, or limitation of access, shall be made at our sole
              discretion and we shall not be liable to you or any third party
              with regards to the termination of your account and access to our
              platform.
            </p>
          </div>
        ),
      },
      {
        id: "disclaimers-no-warranties",
        title: "Disclaimers; No Warranties",
        content: (
          <div className="policy-clauses">
            <p>
              17.1. SAVE AS OTHERWISE AGREED IN WRITING BY PARTIES, REEDDI DOES
              NOT WARRANT THAT THE SERVICES OR THE SERVERS THAT MAKE THE
              PLATFORM AVAILABLE WILL BE FREE OF VIRUSES OR OTHER HARMFUL
              COMPONENTS, OR THAT ANY PRODUCT DESCRIPTION OR OTHER CONTENT
              OFFERED AS PART OF THE SERVICES ARE ACCURATE, RELIABLE, CURRENT OR
              COMPLETE.
            </p>
            <p>
              17.2. YOU EXPRESSLY AGREE THAT YOUR USE OF THE PLATFORM IS AT YOUR
              SOLE RISK. IF YOU DOWNLOAD ANY CONTENT ON THE PLATFORM, YOU DO SO
              AT YOUR OWN DISCRETION AND RISK. YOU WILL BE SOLELY RESPONSIBLE
              FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM OR LOSS OF DATA THAT
              RESULTS FROM THE DOWNLOAD OF SUCH CONTENT THROUGH THE PLATFORM.
            </p>
            <p>
              17.3. YOU AGREE TO DEFEND AND HOLD REEDDI AND ANY OF ITS
              DIRECTORS, AFFILIATES, EMPLOYEES, AGENTS HARMLESS AGAINST ANY AND
              ALL LEGAL CLAIMS AND DEMANDS, INCLUDING REASONABLE ATTORNEY’S
              FEES, WHICH MAY ARISE FROM OR RELATE TO YOUR USE OR MISUSE OF THE
              PLATFORM, YOUR BREACH OF THIS ‘TERMS OF USE OR YOUR ACTIONS. YOU
              AGREE THAT REEDDI SHALL BE FREE TO ELECT ITS OWN LEGAL COUNSEL AND
              PARTICIPATE IN ITS OWN DEFENSE IF IT WISHES.
            </p>
          </div>
        ),
      },
      {
        id: "governing-law-and-dispute-settlement",
        title: "Governing Law & Dispute Settlement",
        content: (
          <div className="policy-clauses">
            <p>
              18.1. You agree that this Terms of Use and any contractual
              obligation between you and Reeddi will be governed by the laws of
              the Federal Republic of Nigeria.
            </p>
            <p>
              18.2. If a dispute or difference arises as to the validity,
              interpretation, effects, or rights and obligations of the Parties
              under these Terms and Conditions, the Parties shall use their best
              endeavor to reach an amicable settlement of the dispute.
            </p>
            <p>
              18.3. If any such dispute is not settled between the Parties
              within fourteen (14) days, the Parties agree to submit such
              dispute to Mediation at the Lagos State Multi-Door Courthouse
              (LMDC) for resolution under the provisions of Lagos State
              Multi-Door Courthouse (LMDC) Law 2007.
            </p>
          </div>
        ),
      },
      {
        id: "contact-information",
        title: "Contact Information",
        content: (
          <div>
            <p>
              If you have any questions/complaints concerning Pluck, this Terms
              and Conditions or anything related to any of the foregoing, please
              reach out to us at{" "}
              <a
                href="mailto:hello@reeddi.com"
                className="font-medium text-primary"
              >
                hello@reeddi.com
              </a>
            </p>

            <div className="mt-5 space-y-4">
              <div>
                📍 🇳🇬 Nigeria Office: 32B Okunola Aina Street, Maryland, Lagos
                <div className="mt-1 flex flex-col -space-y-0.5">
                  <a href="tel:+2349113889461">+234-911-388-9461,</a>
                  <a href="tel:+2349158428464">+234-915-842-8464,</a>
                  <a href="tel:+2349113889467">+234-911-388-9467</a>
                </div>
              </div>

              <div>
                📍 🇨🇦 Canada Office: 108 College St, Toronto, ON M5G 1L7 Canada
                <div>
                  <a href="tel:+16474554327">+1 (647) 455 4327</a>
                </div>
              </div>
            </div>
          </div>
        ),
      },
    ],
  },
  policy: {
    title: "Privacy Policy",
    lastUpdatedDate: "Last Updated: 23rd September 2025",
    introText:
      "Pluck (“we,” “our,” “us”) values your privacy. This Privacy Policy explains how we collect, use, share, and protect your personal information when you access or use our website usepluck.com, mobile applications, and services (collectively, the “Services”). By using our Services, you consent to the practices described in this Privacy Policy.",
    sections: [
      {
        id: "information-we-collect",
        title: "Information We Collect",
        content: (
          <>
            <p className="mb-2">
              We may collect the following types of information:
            </p>

            <ul>
              <li>
                Personal Information: Name, phone number, email address,
                business name, and payment details when you sign up or make
                transactions.
              </li>
              <li>
                Business Information: Type of business, equipment requested,
                repayment preferences, and transaction history.
              </li>
              <li>
                Device Information: IP address, browser type, operating system,
                and usage data.
              </li>
              <li>
                Location Information: Approximate location data to connect you
                with local agents and vendors.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "how-we-use-your-information",
        title: "How We Use Your Information",
        content: (
          <>
            <p className="mb-2">We use your information to:</p>

            <ul>
              <li>Provide, operate, and improve our Services.</li>
              <li>Process payments and installment plans.</li>
              <li>Verify identity and prevent fraud.</li>
              <li>Connect you with vendors, agents, or service partners.</li>
              <li>
                Communicate with you about your account, transactions, and
                support requests.
              </li>
              <li>
                Send you relevant updates, promotions, or product information
                (with your consent)
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "sharing-of-information",
        title: "Sharing of Information",
        content: (
          <>
            <p className="mb-2">
              We do not sell your personal information. However, we may share it
              with:
            </p>

            <ul>
              <li>
                <span className="font-semibold">Vendors/Manufacturers:</span> To
                fulfill equipment requests.
              </li>
              <li>
                <span className="font-semibold">Local Agents:</span> To
                facilitate onboarding, verification, and support.
              </li>
              <li>
                <span className="font-semibold">Service Providers:</span> Such
                as payment processors, cloud hosting, and analytics providers.
              </li>
              <li>
                <span className="font-semibold">
                  Regulators/Law Enforcement:
                </span>
                When required by law or to protect against fraud and misuse.
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "data-protection-and-security",
        title: "Data Protection & Security",
        content: (
          <p>
            We implement reasonable administrative, technical, and physical
            safeguards to protect your information. However, no method of
            electronic storage or transmission is 100% secure, so we cannot
            guarantee absolute security.
          </p>
        ),
      },
      {
        id: "your-rights",
        title: "Your Rights",
        content: (
          <>
            <p className="mb-2">
              Depending on your location, you may have the right to:
            </p>

            <ul>
              <li>Access and request a copy of your personal data.</li>
              <li>
                Request correction or deletion of inaccurate or unnecessary
                data.
              </li>
              <li>Withdraw consent for certain processing activities.</li>
              <li>Opt out of marketing communications.</li>
              <li>
                To exercise these rights, please contact us at{" "}
                <a
                  href="mailto:contact@usepluck.com"
                  className="font-semibold text-primary"
                >
                  contact@usepluck.com
                </a>
              </li>
            </ul>
          </>
        ),
      },
      {
        id: "cookies-and-tracking",
        title: "Cookies and Tracking",
        content: (
          <p>
            We may use cookies and similar technologies to improve user
            experience, analyze traffic, and personalize content. You can manage
            cookies in your browser settings.
          </p>
        ),
      },
      {
        id: "data-retention",
        title: "Data Retention",
        content: (
          <p>
            We retain your information only as long as necessary to provide
            Services, comply with legal obligations, or resolve disputes.
          </p>
        ),
      },
      {
        id: "third-party-links",
        title: "Third-Party Links",
        content: (
          <p>
            Our Services may contain links to third-party websites. We are not
            responsible for the privacy practices of those sites.
          </p>
        ),
      },
      {
        id: "updates-to-this-policy",
        title: "Updates to This Policy",
        content: (
          <p>
            We may update this Privacy Policy from time to time. Changes will be
            posted on this page with the updated date.
          </p>
        ),
      },
      {
        id: "contact-us",
        title: "Contact Information",
        content: (
          <div>
            <ul>
              <li>
                For questions or concerns about these Terms, please contact us
                at: 📧{" "}
                <a
                  href="mailto:hello@usepluck.com"
                  className="font-medium text-primary"
                >
                  hello@usepluck.com
                </a>
              </li>
            </ul>

            <div className="mt-5 space-y-4 pl-6">
              <div>
                📍 🇳🇬 Nigeria Office: 32B Okunola Aina Street, Maryland, Lagos
                <div className="mt-1 flex flex-col -space-y-0.5">
                  <a href="tel:+2349113889461">+234-911-388-9461,</a>
                  <a href="tel:+2349158428464">+234-915-842-8464,</a>
                  <a href="tel:+2349113889467">+234-911-388-9467</a>
                </div>
              </div>

              <div>
                📍 🇨🇦 Canada Office: 108 College St, Toronto, ON M5G 1L7 Canada
                <div>
                  <a href="tel:+16474554327">+1 (647) 455 4327</a>
                </div>
              </div>
            </div>
          </div>
        ),
      },
    ],
  },
};

const socialLinks = [
  {
    href: "https://www.instagram.com/pluckafrica/",
    label: "Instagram",
    icon: Instagram,
  },
  // { href: "/", label: "LinkedIn", icon: LinkedIn },
];

export {
  miscellaneousLinks,
  navItems,
  navItems2,
  policyContent,
  whatsappSupportLink,
  contactOffices,
  contactEmailItems,
  contactUsItems,
  socialLinks,
};
